import React, { useState, useRef } from 'react';
import { TextField, ActionButton, Icon, DefaultButton, PrimaryButton, Dialog, DialogType, DialogFooter, ComboBox, Checkbox, Pivot, PivotItem } from '@fluentui/react';
import { useTranslation } from 'react-i18next';

interface ZodField {
    id: string;
    name: string;
    type: 'string' | 'number' | 'boolean' | 'array' | 'object' | 'date' | 'email' | 'url';
    required: boolean;
    description?: string;
    defaultValue?: string;
    children?: ZodField[];
    isArray?: boolean;
    arrayType?: 'string' | 'number' | 'boolean' | 'object';
}

interface ZodEditorProps {
    value: string;
    onChange: (value: string, fields: ZodField[]) => void;
    label?: string;
    placeholder?: string;
    initialFields?: ZodField[];
}

export default function ZodEditor({ value, onChange, label, placeholder, initialFields }: ZodEditorProps) {
    const { t } = useTranslation();
    const [fields, setFields] = useState<ZodField[]>(initialFields || []);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOverField, setDragOverField] = useState<string | null>(null);
    const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
    const [newField, setNewField] = useState<Partial<ZodField>>({});
    const [editingField, setEditingField] = useState<ZodField | null>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);
    const nameFieldRef = useRef<any>(null);
    const [nextFieldId, setNextFieldId] = useState(1);

    // Generate unique field ID with timestamp to ensure uniqueness
    const generateFieldId = () => {
        const id = `field-${Date.now()}-${nextFieldId}`;
        setNextFieldId(nextId => nextId + 1);
        return id;
    };

    const fieldTypes = [
        { key: 'string', text: 'String', icon: 'TextField' },
        { key: 'number', text: 'Number', icon: 'NumberSymbol' },
        { key: 'boolean', text: 'Boolean', icon: 'CheckboxComposite' },
        { key: 'array', text: 'Array', icon: 'BulletedList' },
        { key: 'object', text: 'Object', icon: 'Folder' },
        { key: 'date', text: 'Date', icon: 'Calendar' },
        { key: 'email', text: 'Email', icon: 'Mail' },
        { key: 'url', text: 'URL', icon: 'Link' }
    ];

    const getNextFieldNumber = (type: string, fieldList: ZodField[]): number => {
        const existingFields = fieldList.filter(field => field.name.startsWith(`${type}-`));
        if (existingFields.length === 0) return 1;
        
        const numbers = existingFields.map(field => {
            const match = field.name.match(new RegExp(`^${type}-(\\d+)$`));
            return match ? parseInt(match[1]) : 0;
        });
        
        return Math.max(...numbers) + 1;
    };

    const generateZodSchema = (fields: ZodField[], indentLevel: number = 0): string => {
        if (fields.length === 0) return 'z.object({})';
        
        const indent = '  '.repeat(indentLevel);
        const nextIndent = '  '.repeat(indentLevel + 1);
        
        const generateFieldSchema = (field: ZodField, level: number = 0): string => {
            let schema = '';
            
            switch (field.type) {
                case 'string':
                    schema = 'z.string()';
                    break;
                case 'number':
                    schema = 'z.number()';
                    break;
                case 'boolean':
                    schema = 'z.boolean()';
                    break;
                case 'date':
                    schema = 'z.date()';
                    break;
                case 'email':
                    schema = 'z.string().email()';
                    break;
                case 'url':
                    schema = 'z.string().url()';
                    break;
                case 'array':
                    if (field.arrayType === 'string') {
                        schema = 'z.array(z.string())';
                    } else if (field.arrayType === 'number') {
                        schema = 'z.array(z.number())';
                    } else if (field.arrayType === 'boolean') {
                        schema = 'z.array(z.boolean())';
                    } else if (field.arrayType === 'object') {
                        schema = 'z.array(z.object({}))';
                    } else {
                        schema = 'z.array(z.string())';
                    }
                    break;
                case 'object':
                    if (field.children && field.children.length > 0) {
                        schema = generateZodSchema(field.children, level + 1);
                    } else {
                        schema = 'z.object({})';
                    }
                    break;
                default:
                    schema = 'z.string()';
            }

            if (field.description) {
                console.log('Adding description to schema:', field.description);
                schema += `.describe("${field.description}")`;
            }

            if (field.defaultValue !== undefined && field.defaultValue !== '') {
                if (field.type === 'string') {
                    schema += `.default("${field.defaultValue}")`;
                } else if (field.type === 'number') {
                    schema += `.default(${field.defaultValue})`;
                } else if (field.type === 'boolean') {
                    schema += `.default(${field.defaultValue})`;
                }
            }

            if (!field.required) {
                schema += '.optional()';
            }

            return schema;
        };

        const schemaFields = fields.map(field => 
            `${nextIndent}${field.name}: ${generateFieldSchema(field, indentLevel)}`
        ).join(',\n');

        return `z.object({\n${schemaFields}\n${indent}})`;
    };

    // Convert fields to JSON structure
    const fieldsToJson = (fields: ZodField[]): any => {
        const result: any = {};
        
        fields.forEach(field => {
            let value: any;
            
            switch (field.type) {
                case 'string':
                    value = field.defaultValue || '';
                    break;
                case 'number':
                    value = field.defaultValue ? parseFloat(field.defaultValue) : 0;
                    break;
                case 'boolean':
                    value = field.defaultValue === 'true';
                    break;
                case 'object':
                    value = field.children && field.children.length > 0 ? fieldsToJson(field.children) : {};
                    break;
                default:
                    value = '';
            }
            
            if (!field.required) {
                result[field.name] = value;
            } else {
                result[field.name] = value;
            }
        });
        
        return result;
    };

    const handleDragStart = (e: React.DragEvent, fieldType: string) => {
        e.dataTransfer.setData('fieldType', fieldType);
        setIsDragging(true);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        const target = e.target as HTMLElement;
        const fieldId = target.closest('[data-field-id]')?.getAttribute('data-field-id') || null;
        
        if (fieldId) {
            // Check if the target field is an object type
            const findTargetField = (fieldList: ZodField[]): ZodField | null => {
                for (const field of fieldList) {
                    if (field.id === fieldId) {
                        return field;
                    }
                    if (field.children) {
                        const found = findTargetField(field.children);
                        if (found) return found;
                    }
                }
                return null;
            };
            
            const targetField = findTargetField(fields);
            
            // Only allow dropping into object fields
            if (targetField && targetField.type === 'object') {
                setDragOverField(fieldId);
            } else {
                setDragOverField(null);
            }
        } else {
            setDragOverField(null);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        setDragOverField(null);
        
        const fieldType = e.dataTransfer.getData('fieldType') as ZodField['type'];
        const target = e.target as HTMLElement;
        const targetFieldId = target.closest('[data-field-id]')?.getAttribute('data-field-id');
        
        if (targetFieldId) {
            // Find the target field to check its type
            const findTargetField = (fieldList: ZodField[]): ZodField | null => {
                for (const field of fieldList) {
                    if (field.id === targetFieldId) {
                        return field;
                    }
                    if (field.children) {
                        const found = findTargetField(field.children);
                        if (found) return found;
                    }
                }
                return null;
            };
            
            const targetField = findTargetField(fields);
            
            // Add as child of target field
            const addFieldToParent = (parentId: string, newField: ZodField, fieldList: ZodField[]): ZodField[] => {
                return fieldList.map(field => {
                    if (field.id === parentId) {
                        // Only allow dropping into object fields
                        if (field.type === 'object') {
                            return {
                                ...field,
                                children: [...(field.children || []), newField]
                            };
                        } else {
                            // If target field is not an object, don't allow dropping
                            console.log('Cannot drop into non-object field:', field.name, 'type:', field.type);
                            return field;
                        }
                    } else if (field.children) {
                        return {
                            ...field,
                            children: addFieldToParent(parentId, newField, field.children)
                        };
                    }
                    return field;
                });
            };
            
            const getNextNumberInTree = (type: string, fieldList: ZodField[]): number => {
                let maxNumber = 0;
                const findMaxNumber = (fields: ZodField[]) => {
                    fields.forEach(field => {
                        if (field.name.startsWith(`${type}-`)) {
                            const match = field.name.match(new RegExp(`^${type}-(\\d+)$`));
                            if (match) {
                                maxNumber = Math.max(maxNumber, parseInt(match[1]));
                            }
                        }
                        if (field.children) {
                            findMaxNumber(field.children);
                        }
                    });
                };
                findMaxNumber(fieldList);
                return maxNumber + 1;
            };
            
            const newField: ZodField = {
                id: generateFieldId(),
                name: `${fieldType}-${getNextNumberInTree(fieldType, fields)}`,
                type: fieldType,
                required: true
            };
            
            setFields(addFieldToParent(targetFieldId, newField, fields));
        } else {
            // Add to root level
            const newField: ZodField = {
                id: generateFieldId(),
                name: `${fieldType}-${getNextFieldNumber(fieldType, fields)}`,
                type: fieldType,
                required: true
            };
            setFields([...fields, newField]);
        }
    };

    const handleDragLeave = () => {
        setIsDragging(false);
        setDragOverField(null);
    };

    const addField = () => {
        if (newField.type) {
            const fieldName = newField.name || `${newField.type}-${getNextFieldNumber(newField.type, fields)}`;
            const field: ZodField = {
                id: generateFieldId(),
                name: fieldName,
                type: newField.type as ZodField['type'],
                required: newField.required || false,
                description: newField.description,
                defaultValue: newField.defaultValue,
                isArray: newField.isArray,
                arrayType: newField.arrayType as ZodField['arrayType']
            };
            console.log('Adding field with description:', field.description);
            setFields([...fields, field]);
            setNewField({});
            setAddFieldDialogOpen(false);
        }
    };

    const editField = (field: ZodField) => {
        setEditingField(field);
        setNewField({
            name: field.name,
            type: field.type,
            required: field.required,
            description: field.description,
            defaultValue: field.defaultValue,
            isArray: field.isArray,
            arrayType: field.arrayType
        });
        setAddFieldDialogOpen(true);
    };

    const updateField = () => {
        if (editingField && newField.name && newField.type) {
            const updateFieldInList = (fieldList: ZodField[]): ZodField[] => {
                return fieldList.map(field => {
                    if (field.id === editingField.id) {
                        return {
                            ...field,
                            name: newField.name!,
                            type: newField.type as ZodField['type'],
                            required: newField.required || false,
                            description: newField.description,
                            defaultValue: newField.defaultValue,
                            isArray: newField.isArray,
                            arrayType: newField.arrayType as ZodField['arrayType']
                        };
                    } else if (field.children) {
                        return {
                            ...field,
                            children: updateFieldInList(field.children)
                        };
                    }
                    return field;
                });
            };
            
            setFields(updateFieldInList(fields));
            setEditingField(null);
            setNewField({});
            setAddFieldDialogOpen(false);
        }
    };

    const deleteField = (fieldId: string) => {
        const removeFieldFromList = (fieldList: ZodField[]): ZodField[] => {
            return fieldList.filter(field => {
                if (field.id === fieldId) {
                    return false;
                } else if (field.children) {
                    field.children = removeFieldFromList(field.children);
                }
                return true;
            });
        };
        
        setFields(removeFieldFromList(fields));
    };

    const renderField = (field: ZodField, depth: number = 0) => {
        const fieldType = fieldTypes.find(ft => ft.key === field.type);
        
        return (
            <div
                key={field.id}
                data-field-id={field.id}
                className={`field-item ${dragOverField === field.id ? 'drag-over' : ''}`}
                style={{ 
                    marginLeft: `${depth * 20}px`,
                    padding: '8px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    marginBottom: '4px',
                    backgroundColor: '#f9f9f9'
                }}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragLeave={handleDragLeave}
            >
                <div className="displayFlex alignItemsCenter justifyContentSpaceBetween">
                    <div className="displayFlex alignItemsCenter gapS">
                        <Icon iconName={fieldType?.icon || 'TextField'} />
                        <span style={{ fontWeight: 'bold' }}>{field.name}</span>
                        <span style={{ color: '#666' }}>({field.type})</span>
                        {field.required && <span style={{ color: 'red' }}>*</span>}
                        {field.isArray && <span style={{ color: '#0078d4' }}>[Array]</span>}
                    </div>
                    <div className="displayFlex gapS">
                        <ActionButton
                            iconProps={{ iconName: 'Edit' }}
                            onClick={() => editField(field)}
                            title="Bearbeiten"
                        />
                        <ActionButton
                            iconProps={{ iconName: 'Delete' }}
                            onClick={() => deleteField(field.id)}
                            title="Löschen"
                            styles={{
                                root: { color: '#A4262C' },
                                icon: { color: '#A4262C' }
                            }}
                        />
                    </div>
                </div>
                {field.description && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {field.description}
                    </div>
                )}
                {field.defaultValue && (
                    <div style={{ fontSize: '11px', color: '#0078d4', marginTop: '2px', fontStyle: 'italic' }}>
                        Default: {field.defaultValue}
                    </div>
                )}
                {field.children && field.children.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                        {field.children.map(child => renderField(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    // Track the current prompt tool ID to know when we switch between different tools
    const [currentPromptToolId, setCurrentPromptToolId] = React.useState<string | null>(null);
    
    // Update fields when initialFields changes (only when switching between different prompt tools)
    React.useEffect(() => {
        // Check if we're switching to a different prompt tool
        const newPromptToolId = initialFields ? JSON.stringify(initialFields) : null;
        
        if (newPromptToolId !== currentPromptToolId) {
            console.log('ZodEditor: Switching to different prompt tool, updating fields');
            setCurrentPromptToolId(newPromptToolId);
            setFields(initialFields || []);
        }
    }, [initialFields, currentPromptToolId]);

    // Update the Zod schema whenever fields change
    React.useEffect(() => {
        const schema = generateZodSchema(fields);
        console.log('ZodEditor: Generated schema with fields:', fields);
        console.log('ZodEditor: Generated schema string:', schema);
        onChange(schema, fields);
    }, [fields]); // Removed onChange from dependencies

    // Auto-select text in name field when dialog opens
    React.useEffect(() => {
        if (addFieldDialogOpen) {
            setTimeout(() => {
                if (nameFieldRef.current) {
                    nameFieldRef.current.select();
                }
            }, 100);
        }
    }, [addFieldDialogOpen]);



    return (
        <div>
            <div className="marginBottomM">
                {label && <div className="textM marginBottomS">{label}</div>}
                <div className="displayFlex gapM">
                    <div className="field-types-panel" style={{ 
                        width: '150px', 
                        padding: '8px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: '4px',
                        backgroundColor: '#f5f5f5'
                    }}>
                        <div className="textS marginBottomS" style={{ fontWeight: 'bold' }}>Feldtypen</div>
                        {fieldTypes.map(type => (
                            <div
                                key={type.key}
                                draggable
                                onDragStart={(e) => handleDragStart(e, type.key)}
                                style={{
                                    padding: '6px',
                                    marginBottom: '3px',
                                    border: '1px solid #d0d0d0',
                                    borderRadius: '4px',
                                    backgroundColor: 'white',
                                    cursor: 'grab',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '12px'
                                }}
                            >
                                <Icon iconName={type.icon} style={{ fontSize: '12px' }} />
                                <span>{type.text}</span>
                            </div>
                        ))}
                    </div>
                    
                    <div
                        ref={dropZoneRef}
                        className="drop-zone"
                        style={{
                            flex: '1 1 50%',
                            minHeight: '150px',
                            border: '2px dashed #ccc',
                            borderRadius: '4px',
                            padding: '12px',
                            backgroundColor: isDragging ? '#f0f8ff' : 'white'
                        }}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onDragLeave={handleDragLeave}
                    >
                        {fields.length === 0 ? (
                            <div className="displayFlex flexDirectionColumn alignItemsCenter justifyContentCenter" style={{ height: '100%', color: '#666' }}>
                                <Icon iconName="Upload" style={{ fontSize: '20px', marginBottom: '6px' }} />
                                <div style={{ fontSize: '12px' }}>Feldtypen hierher ziehen oder</div>
                                <ActionButton onClick={() => setAddFieldDialogOpen(true)}>
                                    Feld hinzufügen
                                </ActionButton>
                            </div>
                        ) : (
                            <div>
                                <div className="displayFlex justifyContentSpaceBetween alignItemsCenter marginBottomM">
                                    <div className="textS" style={{ fontWeight: 'bold' }}>Schema Felder</div>
                                    <ActionButton onClick={() => setAddFieldDialogOpen(true)}>
                                        Feld hinzufügen
                                    </ActionButton>
                                </div>
                                {fields.map(field => renderField(field))}
                            </div>
                        )}
                    </div>

                    <div className="zod-schema-panel" style={{ 
                        flex: '1 1 50%',
                        padding: '8px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa'
                    }}>
                        <div className="textS marginBottomS" style={{ fontWeight: 'bold' }}>Generiertes Schema:</div>
                        <TextField
                            multiline
                            rows={12}
                            value={generateZodSchema(fields)}
                            readOnly
                            styles={{
                                field: {
                                    fontFamily: 'monospace',
                                    fontSize: '11px',
                                    backgroundColor: 'white'
                                }
                            }}
                        />
                    </div>
                </div>
            </div>

            <Dialog
                hidden={!addFieldDialogOpen}
                onDismiss={() => {
                    setAddFieldDialogOpen(false);
                    setEditingField(null);
                    setNewField({});
                }}
                maxWidth={500}
                minWidth={500}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: editingField ? 'Feld bearbeiten' : 'Neues Feld hinzufügen',
                }}
                modalProps={{
                    isBlocking: true,
                }}
            >
                <div className="marginTopM">
                    <TextField
                        label="Feldname (optional)"
                        value={newField.name || ''}
                        onChange={(_, value) => setNewField({ ...newField, name: value || '' })}
                        placeholder="Leer lassen für automatische Benennung"
                        componentRef={nameFieldRef}
                    />
                    <ComboBox
                        label="Feldtyp"
                        selectedKey={newField.type}
                        options={fieldTypes.map(type => ({ key: type.key, text: type.text }))}
                        onChange={(_, option) => option && setNewField({ ...newField, type: option.key as ZodField['type'] })}
                        required
                    />
                    <TextField
                        label="Beschreibung (optional)"
                        value={newField.description || ''}
                        onChange={(_, value) => setNewField({ ...newField, description: value || '' })}
                        multiline
                        rows={2}
                    />
                    {(newField.type === 'string' || newField.type === 'number' || newField.type === 'boolean' || newField.type === 'email' || newField.type === 'url') && (
                        <TextField
                            label="Standardwert (optional)"
                            value={newField.defaultValue || ''}
                            onChange={(_, value) => setNewField({ ...newField, defaultValue: value || '' })}
                            placeholder={newField.type === 'string' ? 'z.B. "Standard Text"' : newField.type === 'number' ? 'z.B. 42' : newField.type === 'boolean' ? 'z.B. true' : ''}
                        />
                    )}
                    <div className="marginTopS">
                        <Checkbox
                            label="Erforderlich"
                            checked={newField.required || false}
                            onChange={(_, checked) => setNewField({ ...newField, required: checked || false })}
                        />
                    </div>
                    {newField.type === 'array' && (
                        <ComboBox
                            label="Array Element Typ"
                            selectedKey={newField.arrayType}
                            options={[
                                { key: 'string', text: 'String' },
                                { key: 'number', text: 'Number' },
                                { key: 'boolean', text: 'Boolean' },
                                { key: 'object', text: 'Object' }
                            ]}
                            onChange={(_, option) => option && setNewField({ ...newField, arrayType: option.key as ZodField['arrayType'] })}
                            required
                        />
                    )}
                </div>
                <DialogFooter className="marginTopXXL">
                    <DefaultButton onClick={() => {
                        setAddFieldDialogOpen(false);
                        setEditingField(null);
                        setNewField({});
                    }} text="Abbrechen" />
                    <PrimaryButton onClick={editingField ? updateField : addField} text={editingField ? "Aktualisieren" : "Hinzufügen"} />
                </DialogFooter>
            </Dialog>
        </div>
    );
} 